/**
 * Central query configuration defaults.
 * Import these in the QueryClient setup and per-query overrides.
 */
import { getUniversesQueryKey } from '@biasafe.queries/securities-universes-data/securities-universes-data';
import { getUniverseSecurityItemsQueryKey } from '@biasafe.queries/securities-universes-security-items/securities-universes-security-items';

/** Profile data rarely changes — cache for 5 minutes */
export const PROFILE_STALE_TIME = 5 * 60 * 1000;

/** Static reference data (universes, classifications) — cache for 24 hours */
export const STATIC_DATA_STALE_TIME = 24 * 60 * 60 * 1000;

/** Default stale time for all queries — 30 seconds */
export const DEFAULT_STALE_TIME = 30 * 1000;

/** How often to re-probe while a universe query is missing its numbers. */
export const UNIVERSE_RECOVERY_POLL_MS = 15 * 1000;

/**
 * Shared options for the two universe queries.
 *
 * Both are served from a static catalogue and enriched with ClickHouse numbers,
 * so a response can arrive HTTP-200 and still be missing its measurements.
 * `retry` never fires for that — as far as the transport is concerned it
 * succeeded — which is why recovery is driven by the refetch options below and
 * not by retries.
 *
 * The freshness policy is therefore conditional on the payload:
 *
 *   complete → cached for 24h. The securities query scans ~8.6k rows (~2.4s);
 *              re-running it on every step change is pure waste.
 *   degraded → treated as immediately stale, so remounting (moving between
 *              wizard steps), refocusing the tab, or simply waiting all
 *              re-probe until the numbers land.
 *
 * EVERY caller of these queries must spread the matching object. They share a
 * queryKey, and staleTime is evaluated PER OBSERVER: one call site that forgets
 * silently sets the policy for all the others.
 */
function universeQueryOptions<T>(isDegraded: (data: T | undefined) => boolean) {
  const degraded = (query: { state: { data?: T } }) =>
    isDegraded(query.state.data);

  return {
    staleTime: (query: { state: { data?: T } }) =>
      degraded(query) ? 0 : STATIC_DATA_STALE_TIME,
    gcTime: STATIC_DATA_STALE_TIME,
    // Transport-level failures only (network drop, 5xx). One retry absorbs a
    // blip without turning a real outage into a burst.
    retry: 1,
    refetchOnWindowFocus: (query: { state: { data?: T } }) => degraded(query),
    refetchOnMount: (query: { state: { data?: T } }) =>
      degraded(query) ? ('always' as const) : true,
    // Polls ONLY while the numbers are missing, and stops the moment they
    // arrive — a healthy session never issues a single extra request.
    refetchInterval: (query: { state: { data?: T } }) =>
      degraded(query) ? UNIVERSE_RECOVERY_POLL_MS : (false as const),
    // Don't keep hitting the API from a tab nobody is looking at; the focus
    // refetch covers the user's return.
    refetchIntervalInBackground: false,
  };
}

/**
 * Catalogue query (`/securities/universes/data`). The six indices are static
 * and always present, so "degraded" means the CH-derived numbers are absent —
 * every index reporting a zero market cap.
 *
 * A FUNCTION of the as-of date rather than a constant, because the key has to
 * carry that date. The wizard resolves the universe at step 2's latest
 * reconstitution date, so the eligible universe and the portfolio it will
 * rebalance are counted at the same instant. Surfaces that legitimately want
 * today — the benchmark hub, a finished report — call it with no argument and
 * keep exactly the key they had.
 *
 * The key still travels WITH the options rather than being left to each call
 * site: orval's generated options type requires `queryKey`, so every caller
 * would otherwise repeat it, and pairing these options with a mismatched key
 * silently splits the cache.
 *
 * This is also what makes a schedule change refetch ACROSS steps. Nothing has
 * to invalidate anything: a different date is a different key, so no observer
 * can serve the previous date's universe from cache.
 */
export function universeCatalogueQuery(asOf?: string | null) {
  return {
    queryKey: getUniversesQueryKey(asOf ? { as_of: asOf } : undefined),
    ...universeQueryOptions<Array<{ marketCap: number }>>(
      (data) => !data?.length || data.every((u) => !u.marketCap),
    ),
  };
}

/**
 * Securities query (`/securities/universes/security-items`). Nothing here is
 * static, so an empty list IS the degraded state. Same as-of contract as the
 * catalogue above.
 *
 * The previous constant left `queryKey` to each call site, and all four passed
 * `getUniverseSecurityItemsQueryKey()` with no arguments — a key that ignored
 * the params entirely. Adding a date without folding the key in here would have
 * changed the request and kept the cache entry, which is the one failure mode
 * this whole change exists to remove.
 */
export function universeSecuritiesQuery(asOf?: string | null) {
  return {
    queryKey: getUniverseSecurityItemsQueryKey(
      asOf ? { as_of: asOf } : undefined,
    ),
    ...universeQueryOptions<Array<unknown>>((data) => !data?.length),
  };
}
